<template>
  <teleport to="body">
    <transition leave-active-class="duration-200">
      <div
        v-show="show"
        class="fixed inset-0 overflow-y-auto px-4 py-6 sm:px-0 z-50"
      >
        <transition
          enter-active-class="ease-out duration-300"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-show="show"
            class="fixed inset-0 transform transition-all"
            @click="cancel"
          >
            <div class="absolute inset-0 bg-gray-500 opacity-75" />
          </div>
        </transition>

        <transition
          enter-active-class="ease-out duration-300"
          enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to-class="opacity-100 translate-y-0 sm:scale-100"
          leave-active-class="ease-in duration-200"
          leave-from-class="opacity-100 translate-y-0 sm:scale-100"
          leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            v-show="show"
            class="shadow-xl transform transition-all sm:mx-auto"
            :class="size"
          >
            <main class="bg-white rounded-lg h-144">
              <div class="w-full h-full flex">
                <div class="w-1/2 flex flex-col pt-6 px-6">
                  <div class="w-full items-center">
                    <h2 class="text-lg font-semibold">
                      Find activities in {{ destination.name }}
                    </h2>
                    <p class="text-sm text-gray-500">
                      Choose from the most popular, search or choose from the list
                    </p>
                    <input
                      v-model="search"
                      type="search"
                      placeholder="Search"
                      class="my-6 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div
                    class="w-full min-h-96 space-y-3 overflow-y-auto mb-2 pr-3"
                  >
                    <p class="text-sm text-gray-500 mb-2">
                      Custom activities
                    </p>
                    <div
                        v-if="loading_custom"
                        class="cursor-disabled select-none flex justify-center items-center">
                      <svg
                          class="w-20 h-20 text-gray-200 animate-spin dark:text-gray fill-blue"
                          viewBox="0 0 100 101"
                          xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0
                        22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144
                        50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186
                        50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor" />
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871
                        24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194
                        63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613
                        1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511
                        9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735
                        17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083
                        38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill" />
                      </svg>
                    </div>
                    <template v-else>
                      <button
                          @click="startCreating"
                          class="w-full group shadow-md rounded-md flex flex-row items-center justify-between border border-white hover:border-primary-dark"
                      >
                        <figure class="overflow-hidden w-18 h-10 flex justify-center items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                          </svg>
                        </figure>

                        <p v-if="!search" class="ml-3 w-full font-semibold text-left">
                          Add new activity
                        </p>
                        <p v-else class="ml-3 w-full font-semibold text-left">
                          Add "{{ search }}"
                        </p>
                        <span class="w-16 mr-3 flex justify-end">
                            <svg
                              v-if="creating"
                                width="14"
                                height="8"
                                viewBox="0 0 14 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                              <path
                                  d="M10.4802 1.40271L13.1815 4.10408M13.1815 4.10408L10.4802 6.80544M13.1815 4.10408L1.02539 4.10407"
                                  stroke="#111827"
                                  stroke-width="1.35068"
                                  stroke-linecap="round"
                                  stroke-linejoin="round" />
                            </svg>
                            <svg
                                v-else
                                width="14"
                                class="hidden group-hover:block"
                                height="8"
                                viewBox="0 0 14 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                              <path
                                  d="M10.4802 1.40271L13.1815 4.10408M13.1815 4.10408L10.4802 6.80544M13.1815 4.10408L1.02539 4.10407"
                                  stroke="#111827"
                                  stroke-width="1.35068"
                                  stroke-linecap="round"
                                  stroke-linejoin="round" />
                            </svg>
                          </span>
                      </button>
                      <button
                          v-for="activity in custom_activities"
                          :key="activity.id"
                          type="button"
                          class="w-full group shadow-md rounded-md flex flex-row items-center justify-between border border-white hover:border-primary-dark"
                          @click="select(activity)">

                        <figure class="overflow-hidden w-18 h-10 flex justify-center items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
                          </svg>
                        </figure>
                        <p class="ml-3 w-full font-semibold text-left">
                          {{ activity.name }}
                        </p>
                        <span class="w-16 mr-3 flex justify-end">
                            <svg
                                v-if="selected !== null && activity.id === selected.id"
                                width="14"
                                height="8"
                                viewBox="0 0 14 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                              <path
                                  d="M10.4802 1.40271L13.1815 4.10408M13.1815 4.10408L10.4802 6.80544M13.1815 4.10408L1.02539 4.10407"
                                  stroke="#111827"
                                  stroke-width="1.35068"
                                  stroke-linecap="round"
                                  stroke-linejoin="round" />
                            </svg>
                            <svg
                                v-else
                                width="14"
                                class="hidden group-hover:block"
                                height="8"
                                viewBox="0 0 14 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                              <path
                                  d="M10.4802 1.40271L13.1815 4.10408M13.1815 4.10408L10.4802 6.80544M13.1815 4.10408L1.02539 4.10407"
                                  stroke="#111827"
                                  stroke-width="1.35068"
                                  stroke-linecap="round"
                                  stroke-linejoin="round" />
                            </svg>
                          </span>
                      </button>
                    </template>
                    <p class="text-sm text-gray-500 mb-2">
                      From Google Maps
                    </p>
                    <div
                        v-if="loading"
                        class="cursor-disabled select-none flex justify-center items-center">
                      <svg
                          class="w-20 h-20 text-gray-200 animate-spin dark:text-gray fill-blue"
                          viewBox="0 0 100 101"
                          xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0
                        22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144
                        50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186
                        50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor" />
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871
                        24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194
                        63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613
                        1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511
                        9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735
                        17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083
                        38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill" />
                      </svg>
                    </div>
                      <button
                          v-else
                          v-for="activity in activities"
                          :key="activity.id"
                        type="button"
                        class="w-full group shadow-md rounded-md flex flex-row items-center justify-between border border-white hover:border-primary-dark"
                        @click="select(activity)">
                        <!--
                        <figure class="overflow-hidden w-20">
                          <img
                            :src="activity.image"
                            :alt="activity.name"
                            class="w-full h-10 object-cover rounded-l-lg">
                        </figure>
                        -->
                        <p class="ml-3 w-full font-semibold text-left">
                          {{ activity.name }}
                        </p>
                        <span class="w-16 mr-3 flex justify-end">
                          <svg
                            v-if="selected !== null && activity.id === selected.id"
                            width="14"
                            height="8"
                            viewBox="0 0 14 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M10.4802 1.40271L13.1815 4.10408M13.1815 4.10408L10.4802 6.80544M13.1815 4.10408L1.02539 4.10407"
                              stroke="#111827"
                              stroke-width="1.35068"
                              stroke-linecap="round"
                              stroke-linejoin="round" />
                          </svg>
                          <svg
                            v-else
                            width="14"
                            class="hidden group-hover:block"
                            height="8"
                            viewBox="0 0 14 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M10.4802 1.40271L13.1815 4.10408M13.1815 4.10408L10.4802 6.80544M13.1815 4.10408L1.02539 4.10407"
                              stroke="#111827"
                              stroke-width="1.35068"
                              stroke-linecap="round"
                              stroke-linejoin="round" />
                          </svg>
                        </span>
                      </button>
                  </div>
                </div>

                <div class="w-1/2 h-full flex flex-col justify-between bg-gray-100 p-6 rounded-r-lg">
                  <div v-if="creating" class="relative">
                    <button
                        @click="creating = false"
                        class="absolute top-0 right-0 hover:text-indigo-900"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>

                    <h2 class="text-lg font-semibold">
                      New Activity
                    </h2>
                    <p class="text-sm text-gray-500">
                      Creating an activity for you and your team. When created, it can be used by anyone in your team.
                    </p>

                    <div class="mt-5">
                      <div class="">
                        <div class="grid grid-cols-6 gap-6">
                          <div class="col-span-6">
                            <label for="name" class="block text-sm font-medium text-gray-700">Name</label>
                            <input v-model="form.name" type="text" name="name" id="name" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                          </div>

                          <div class="col-span-3">
                            <label for="place" class="block text-sm font-medium text-gray-700">Connect to destination</label>
                            <div class="mt-1">
                              <Switch v-model="form.place" class="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                                <span class="sr-only">Connect activity to destination</span>
                                <span aria-hidden="true" class="pointer-events-none absolute h-full w-full rounded-md bg-white" />
                                <span aria-hidden="true" :class="[form.place ? 'bg-indigo-600' : 'bg-gray-200', 'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out']" />
                                <span aria-hidden="true" :class="[form.place ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out']" />
                              </Switch>
                            </div>
                            <p class="mt-2 text-sm text-gray-500">Connect the activity to the destination</p>
                          </div>

                          <div class="col-span-6">
                            <label for="description" class="block text-sm font-medium text-gray-700">Description</label>
                            <div class="mt-1">
                              <textarea v-model="form.description" id="description" name="description" rows="3" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Activity description" />
                            </div>
                            <p class="mt-2 text-sm text-gray-500">Brief description of the activity</p>
                          </div>
                        </div>
                      </div>
                      <div class="py-3 text-right">
                        <button @click="addCustomActivity" type="submit" class="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                          Create and add
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="selected"
                    class="h-full flex flex-col justify-between space-y-8">
                    <div class="w-full h-full bg-white rounded-lg">
                      <figure
                        v-if="selected.image"
                        class="relative overflow-hidden aspect-w-16 aspect-h-8">
                        <img
                          :src="selected.image"
                          :alt="selected.name"
                          class="object-cover rounded-t-lg">
                      </figure>
                      <div class="w-full p-6">
                        <h2 class="mb-4 text-xl font-semibold">
                          {{ selected.name }}
                        </h2>
                        <p class="text-sm text-gray-700">
                          {{ selected.description }}
                        </p>
                      </div>
                    </div>

                    <div class="flex justify-end items-center">
                      <button
                        type="button"
                        class="bg-button-purple text-white text-sm py-1.5 px-3 rounded"
                        @click="addActivity(selected)">
                        Choose
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import {
  ref, onMounted, onUnmounted, watch,
} from 'vue';
import { useForm } from '@inertiajs/vue3';
import Button from '@/Jetstream/Button';
import { Switch } from '@headlessui/vue'


const selected = ref(null);
const creating = ref(false);
const emit = defineEmits(['activity', 'customActivity', 'cancel']);

const form = useForm({
  name: '',
  description: '',
  location: null,
  place: false,
  data: {}
});

const activities = ref([]);
const custom_activities = ref([]);
const search = ref('');
const loading = ref(false);
const loading_custom = ref(false);
let timer;
let custom_timer;

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
    default: false,
  },
  size: {
    type: String,
    default: 'max-w-6xl',
  },
  destination: {
    type: Object,
    required: true,
  },
});

const closeOnEscape = (e) => {
  if (e.key === 'Escape' && props.show) {
    cancel();
  }
};

const cancel = () => {
  emit('cancel');
};

const addActivity = (activity) => {
  emit('activity', activity);
};

const addCustomActivity = () => {
  axios.post(route('api.custom-itinerary.store'), {
    name: form.name,
    description: form.description,
    type: 'activity',
    place_id: form.place ? props.destination.id : null,
  }).then((response) => {
    emit('customActivity', response.data);
  }).catch((error) => {
    console.log(error);
  });
};

const updateSearch = (skipTimer = false) => {
  loading.value = true;
  loading_custom.value = true;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  timer = setTimeout(() => {
    axios.get(`${route('api.activities')}?destination=${props.destination.id}&search=${search.value.length > 0 ? search.value : 'activities'}`)
      .then((response) => {
        activities.value = response.data;
      }).finally(() => {
        loading.value = false;
        timer = null;
      });
  }, skipTimer ? 0 : 500);

  custom_timer = setTimeout(() => {
    axios.get(`${route('api.custom-itinerary.index')}?destination=${props.destination.id}&search=${search.value.length > 0 ? search.value : 'activities'}&type=activity`)
        .then((response) => {
          custom_activities.value = response.data;
        }).finally(() => {
      loading_custom.value = false;
      custom_timer = null;
    });
  }, skipTimer ? 0 : 500);

};

function select(activity) {
  creating.value = false;
  selected.value = activity;
}

function startCreating() {
  selected.value = null;
  form.name = search.value;
  creating.value = true;
}

onMounted(() => document.addEventListener('keydown', closeOnEscape));
onUnmounted(() => document.removeEventListener('keydown', closeOnEscape));

watch(() => props.show, (isOpened) => {
  if (isOpened) {
    updateSearch(true);
  }
});

watch(() => search.value, () => {
  updateSearch();
});

</script>
